import DOMPurify from 'dompurify'

import StoresHeroLandingImageLqip from '@/assets/stores/StoresHeroLandingImage.png?lqip'
import StoresHeroLandingImage from '@/assets/stores/StoresHeroLandingImage.png?minW=1920&picture-image-tools'
import Picture from '@/components/Picture'
import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import {useStores} from '@/lib/store/useStores'
import {StoreRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'

import style from './Stores.module.scss'

interface StoreCardProps {
  store: {
    'id': string
    'name': string
    'description': string
    'logo': string
    'banner-mobile': string
  }
}

function StoreCard({store}: StoreCardProps) {
  return (
    <div role='listitem' className={style.StoreCard}>
      <Link
        to={StoreRoute.fullPath}
        params={{storeId: store.id}}
        className={style.StoreCardLink}
        aria-describedby={`store-${store.id}-desc`}
      >
        <div className={style.StoreCardContainer}>
          <div
            className={style.StoreCardBannerContainer}
            style={{backgroundImage: `url(${store['banner-mobile']})`}}
            role='img'
            aria-hidden='true'
          />
          <div className={style.StoreCardContent}>
            <div className={style.StoreCardHeader}>
              <img src={store.logo} alt='' className={style.StoreCardLogo} loading='lazy' />
              <h3 className={style.StoreCardName}>{store.name}</h3>
            </div>
            <div
              id={`store-${store.id}-desc`}
              className={style.StoreCardDescription}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(store.description, {
                  ALLOWED_TAGS: ['p', 'br', 'a', 'strong', 'em', 'b', 'i'],
                  ALLOWED_ATTR: ['href', 'target', 'rel'],
                  FORBID_ATTR: ['style', 'class', 'id'],
                }),
              }}
            />
          </div>
        </div>
      </Link>
    </div>
  )
}

interface StoresContentProps {
  isLoading: boolean
  isError: boolean
  stores?: {
    'id': string
    'name': string
    'description': string
    'logo': string
    'banner-mobile': string
  }[]
}

function StoresContent({isLoading, isError, stores}: StoresContentProps) {
  if (isLoading) {
    return (
      <div role='status' aria-live='polite' className={style.LoadingState}>
        <p>Loading stores...</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div role='alert' className={style.ErrorState}>
        <p>There was an error fetching the stores. Please try again later.</p>
      </div>
    )
  }

  return (
    <div className={style.StoresGrid} role='list' aria-label='Featured stores'>
      {stores?.map(store => (
        <StoreCard key={store.id} store={store} />
      ))}
    </div>
  )
}

export default function Stores() {
  const {data: stores, isLoading, isError} = useStores()

  return (
    <div className={style.StoresPage}>
      <HeadTags title='Stores' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <Navbar variant='dark' withSearch />
      <main {...skipTargetProps('main-content')} className={style.Main}>
        <section className={style.Hero}>
          <Picture
            src={{
              pic: StoresHeroLandingImage,
              lqip: StoresHeroLandingImageLqip,
            }}
            fetchPriority='high'
            alt=''
            aria-hidden='true'
            className={style.HeroImage}
            sizes='100vw'
            preload
          />
        </section>
        <section className={style.FeaturedStores} aria-labelledby='featured-stores-title'>
          <h1 id='featured-stores-title' className={style.PageTitle}>
            Featured Stores
          </h1>

          <StoresContent isLoading={isLoading} isError={isError} stores={stores} />
        </section>
      </main>
      <Footer />
    </div>
  )
}
